import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import Logo from "./Logo";

const navLinks = [
  { name: "Home", href: "#home" },
  { name: "About", href: "#about" },
  { name: "Services", href: "#services" },
  { name: "Tools", href: "#tools" },
  { name: "Gallery", href: "#gallery" },
  { name: "Journey", href: "#journey" },
  { name: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    navLinks.forEach((link) => {
      const section = document.querySelector(link.href);
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6 px-4">
      <nav
        className={cn(
          "transition-all duration-500 ease-out flex items-center justify-between pl-6 pr-3 py-3 rounded-full border shadow-sm backdrop-blur-xl",
          isScrolled
            ? "bg-white/60 border-white/20 w-full max-w-5xl shadow-lg shadow-stone-500/5"
            : "bg-white/30 border-white/10 w-full max-w-6xl hover:bg-white/50"
        )}
      >
        <a
          href="#home"
          onClick={(e) => scrollToSection(e, "#home")}
          className="text-foreground hover:opacity-80 transition-opacity shrink-0"
        >
          <Logo />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-1">
          {navLinks.map((link) => {
            const isActive = activeSection === link.href.substring(1);
            return (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium rounded-full transition-colors duration-300",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/50"
                )}
              >
                {isActive && (
                  <motion.span
                    layoutId="activeSection"
                    className="absolute inset-0 bg-primary/10 rounded-full -z-10"
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 30,
                    }}
                  />
                )}
                {link.name}
              </a>
            );
          })}
        </div>

        {/* CTA Button (Optional, can be Contact) */}
        <div className="hidden md:block">
          <a
            href="https://cal.com/wellnessbymercy"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-full hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
          >
            Book Now
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2.5 rounded-full bg-white/20 border border-white/20 backdrop-blur-md text-muted-foreground hover:text-foreground hover:bg-white/40 transition-all duration-300 shadow-sm active:scale-95"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.9, y: -20, filter: "blur(10px)" }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.4 }}
            className="absolute top-24 left-4 right-4 bg-white/80 backdrop-blur-2xl rounded-[2.5rem] border border-white/40 shadow-2xl shadow-stone-500/10 p-3 flex flex-col gap-1 origin-top z-40"
          >
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.substring(1);
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={cn(
                    "text-lg font-medium px-6 py-4 rounded-[1.75rem] transition-all duration-300",
                    isActive
                      ? "text-primary bg-primary/10 shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/40"
                  )}
                >
                  {link.name}
                </a>
              );
            })}
            <a
              href="https://cal.com/wellnessbymercy"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 text-center text-lg font-semibold text-white bg-primary px-6 py-4 rounded-[1.75rem] shadow-lg shadow-primary/20 hover:bg-primary/90 transition-colors"
            >
              Book Now
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
